import React, { useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  Collapse,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper
} from '@mui/material';
import {
  Check,
  ChevronRight,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  RocketLaunch
} from '@mui/icons-material';
import OverflowTooltip from '../../shared/OverflowTooltip';
import { groupScrumSprintsByState } from '../../shared/scrumSprintUtils';

const STATE_PRESENTATION = {
  ACTIVE: { label: 'Active', icon: RocketLaunch },
  DRAFT: { label: 'Draft', icon: Edit },
  COMPLETED: { label: 'Completed', icon: Check }
};

const containsId = (ids, id) => ids.some((selectedId) => String(selectedId) === String(id));

const TimesheetScrumSprintSelect = ({
  projects = [],
  sprints = [],
  selectedSprintIds = [],
  onApply,
  disabled = false
}) => {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [draftSelection, setDraftSelection] = useState([]);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [sprintFlyout, setSprintFlyout] = useState(null);

  const availableSprintIds = useMemo(() => sprints.map((sprint) => sprint.id), [sprints]);
  const projectGroups = useMemo(() => projects.map((project) => {
    const projectSprints = sprints.filter((sprint) => (
      String(sprint.project_id) === String(project.id)
    ));
    return {
      project,
      sprints: projectSprints,
      stateGroups: groupScrumSprintsByState(projectSprints)
    };
  }), [projects, sprints]);

  const displayValue = useMemo(() => {
    if (selectedSprintIds.length === 0) return 'All Scrum Sprints';
    if (selectedSprintIds.length === 1) {
      return sprints.find((sprint) => containsId(selectedSprintIds, sprint.id))?.name || '1 Scrum Sprint';
    }
    return `${selectedSprintIds.length} Scrum Sprints`;
  }, [selectedSprintIds, sprints]);

  const beginSelection = () => {
    if (disabled) return;
    setDraftSelection(selectedSprintIds.length === 0 ? availableSprintIds : selectedSprintIds);
    setExpandedProjects(Object.fromEntries(projects.map((project) => [project.id, true])));
    setSprintFlyout(null);
    setOpen(true);
  };

  const cancelSelection = () => {
    setDraftSelection([]);
    setSprintFlyout(null);
    setOpen(false);
  };

  const toggleIds = (ids) => {
    const allSelected = ids.length > 0 && ids.every((id) => containsId(draftSelection, id));
    setDraftSelection((current) => (
      allSelected
        ? current.filter((selectedId) => !ids.some((id) => String(id) === String(selectedId)))
        : [...current, ...ids.filter((id) => !containsId(current, id))]
    ));
  };

  const applySelection = () => {
    const allSelected = availableSprintIds.length > 0
      && availableSprintIds.every((id) => containsId(draftSelection, id));
    onApply(allSelected ? [] : draftSelection);
    setSprintFlyout(null);
    setOpen(false);
  };

  return (
    <>
      <div
        ref={anchorRef}
        role="combobox"
        aria-expanded={open}
        aria-controls={open ? 'timesheet-scrum-sprint-menu' : undefined}
        aria-haspopup="dialog"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={() => (open ? cancelSelection() : beginSelection())}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (open) cancelSelection(); else beginSelection();
          }
        }}
        className={`MuiInputBase-root MuiOutlinedInput-root MuiInputBase-colorPrimary MuiInputBase-formControl MuiInputBase-sizeSmall ${disabled ? 'Mui-disabled' : ''} ${open ? 'Mui-focused' : ''}`}
        style={{
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          width: '100%',
          position: 'relative'
        }}
      >
        <div className="MuiSelect-select MuiSelect-outlined MuiInputBase-input MuiOutlinedInput-input MuiInputBase-inputSizeSmall">
          <OverflowTooltip
            title={displayValue}
            tooltipWidth={350}
            component={Box}
            className="timesheet-filter-value"
            placement="top-start"
          >
            {displayValue}
          </OverflowTooltip>
        </div>
        <fieldset aria-hidden="true" className="MuiOutlinedInput-notchedOutline">
          <legend style={{ display: 'none' }} />
        </fieldset>
      </div>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        className="timesheet-scrum-popper"
        modifiers={[
          { name: 'offset', options: { offset: [0, 5] } },
          { name: 'flip', enabled: true },
          { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 12 } }
        ]}
      >
        <ClickAwayListener onClickAway={cancelSelection}>
          <Paper id="timesheet-scrum-sprint-menu" role="dialog" aria-label="Scrum sprint selection" className="timesheet-scrum-menu" elevation={8}>
            <Box className="timesheet-scrum-menu__projects">
              {projectGroups.map(({ project, sprints: projectSprints, stateGroups }) => {
                const projectIds = projectSprints.map((sprint) => sprint.id);
                const selectedCount = projectIds.filter((id) => containsId(draftSelection, id)).length;
                const allProjectSelected = projectIds.length > 0 && selectedCount === projectIds.length;
                const isExpanded = expandedProjects[project.id] !== false;

                return (
                  <Box key={project.id} className="timesheet-scrum-project">
                    <Box className="timesheet-scrum-project__header">
                      <Checkbox
                        checked={allProjectSelected}
                        indeterminate={selectedCount > 0 && !allProjectSelected}
                        disabled={projectIds.length === 0}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleIds(projectIds)}
                        size="small"
                      />
                      <OverflowTooltip
                        title={project.name}
                        className="timesheet-scrum-project__name"
                        tooltipWidth={350}
                        placement="top-start"
                      />
                      <Box component="span" className="timesheet-scrum-project__count">
                        {selectedCount} Selected
                      </Box>
                      <Button
                        className="timesheet-scrum-project__toggle"
                        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${project.name}`}
                        onClick={() => {
                          setExpandedProjects((current) => ({
                            ...current,
                            [project.id]: !isExpanded
                          }));
                          setSprintFlyout(null);
                        }}
                      >
                        {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </Button>
                    </Box>

                    <Collapse in={isExpanded}>
                      <Box className="timesheet-scrum-project__states">
                        {stateGroups.map((group) => {
                          const groupIds = group.sprints.map((sprint) => sprint.id);
                          const selectedGroupCount = groupIds.filter((id) => containsId(draftSelection, id)).length;
                          const allGroupSelected = selectedGroupCount === groupIds.length;
                          const presentation = STATE_PRESENTATION[group.state] || {
                            label: group.state,
                            icon: Edit
                          };
                          const StateIcon = presentation.icon;

                          return (
                            <Box key={group.state} className="timesheet-scrum-state-line">
                              <Checkbox
                                checked={allGroupSelected}
                                indeterminate={selectedGroupCount > 0 && !allGroupSelected}
                                onChange={() => toggleIds(groupIds)}
                                size="small"
                              />
                              <Box
                                role="button"
                                tabIndex={0}
                                aria-label={`${presentation.label}: ${group.sprints.map((sprint) => sprint.name).join(', ')}`}
                                onClick={() => toggleIds(groupIds)}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    toggleIds(groupIds);
                                  }
                                }}
                                className={`timesheet-scrum-state timesheet-scrum-state--${group.state.toLowerCase()}`}
                              >
                                <Box className="timesheet-scrum-state__icon"><StateIcon /></Box>
                                <Box className="timesheet-scrum-state__content">
                                  <Box className="timesheet-scrum-state__names">
                                    {group.sprints.slice(0, 2).map((sprint) => (
                                      <OverflowTooltip
                                        key={sprint.id}
                                        title={sprint.name || 'Unnamed Sprint'}
                                        className="timesheet-scrum-state__name"
                                        tooltipWidth={350}
                                        placement="top-start"
                                      />
                                    ))}
                                  </Box>
                                  {group.state === 'ACTIVE' && (
                                    <Box component="span" className="timesheet-scrum-state__latest">Latest</Box>
                                  )}
                                </Box>
                                <Box component="span" className="timesheet-scrum-state__badge">
                                  <Box component="span" className="timesheet-scrum-state__dot" />
                                  {presentation.label}
                                </Box>
                                <IconButton
                                  className={`timesheet-scrum-state__expand${sprintFlyout?.group?.state === group.state && String(sprintFlyout?.projectId) === String(project.id) ? ' timesheet-scrum-state__expand--open' : ''}`}
                                  aria-label={`${sprintFlyout?.group?.state === group.state && String(sprintFlyout?.projectId) === String(project.id) ? 'Collapse' : 'Expand'} ${project.name} ${presentation.label} sprints`}
                                  onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    const isSameFlyout = sprintFlyout?.group?.state === group.state
                                      && String(sprintFlyout?.projectId) === String(project.id);
                                    setSprintFlyout(isSameFlyout ? null : {
                                      anchorEl: event.currentTarget,
                                      group,
                                      projectId: project.id,
                                      projectName: project.name,
                                      presentation
                                    });
                                  }}
                                  size="small"
                                >
                                  <ChevronRight className="timesheet-scrum-state__chevron" />
                                </IconButton>
                              </Box>
                            </Box>
                          );
                        })}
                        {stateGroups.length === 0 && (
                          <Box className="timesheet-scrum-project__empty">No sprints available</Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>

            <Box className="timesheet-scrum-menu__actions">
              <Button variant="outlined" onClick={cancelSelection}>Cancel</Button>
              <Button variant="contained" onClick={applySelection}>Apply</Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>

      <Popper
        open={Boolean(sprintFlyout)}
        anchorEl={sprintFlyout?.anchorEl}
        placement="right-start"
        className="timesheet-scrum-individual-popper"
        modifiers={[
          { name: 'offset', options: { offset: [0, 7] } },
          {
            name: 'flip',
            enabled: true,
            options: { fallbackPlacements: ['left-start', 'right-end', 'left-end'] }
          },
          { name: 'preventOverflow', enabled: true, options: { boundary: 'viewport', padding: 8 } }
        ]}
      >
        <ClickAwayListener onClickAway={(event) => { if (anchorRef.current?.contains(event.target)) return; setSprintFlyout(null); }}>
          <Paper className="timesheet-scrum-individual-menu" elevation={8}>
            <Box className="timesheet-scrum-individual-menu__title">
              <OverflowTooltip
                title={`${sprintFlyout?.projectName || ''} · ${sprintFlyout?.presentation?.label || ''} Sprints`}
                className="timesheet-scrum-individual-menu__title-text"
                tooltipWidth={300}
                placement="top-start"
              />
            </Box>
            <MenuList dense>
              {(sprintFlyout?.group?.sprints || []).map((sprint) => (
                <MenuItem
                  key={sprint.id}
                  className={`timesheet-scrum-individual-option timesheet-scrum-individual-option--${sprintFlyout.group.state.toLowerCase()}`}
                  aria-label={`Select ${sprint.name || 'Unnamed Sprint'}`}
                  onClick={(event) => { event.stopPropagation(); toggleIds([sprint.id]); }}
                >
                  <Checkbox
                    checked={containsId(draftSelection, sprint.id)}
                    size="small"
                    tabIndex={-1}
                  />
                  <OverflowTooltip
                    title={sprint.name || 'Unnamed Sprint'}
                    className="timesheet-scrum-individual-option__name"
                    tooltipWidth={300}
                    placement="top-start"
                  />
                </MenuItem>
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

TimesheetScrumSprintSelect.propTypes = {
  projects: PropTypes.arrayOf(PropTypes.object),
  sprints: PropTypes.arrayOf(PropTypes.object),
  selectedSprintIds: PropTypes.array,
  onApply: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export default TimesheetScrumSprintSelect;
